import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Alert,
  AlertIcon,
  Box,
  Button,
  Flex,
  Link,
  Radio,
  RadioGroup,
  Select,
  Spinner,
  Stack,
  Text,
} from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { Link as RouterLink } from "react-router-dom";
import { SERVICE_CATEGORIES, serviceCategoryLabel } from "../../../../../common/procurement/serviceCategory";
import {
  BuyerLegalType,
  ContractNature,
  UtilityActivity,
  FoaPart,
  NoticeRoute,
  Regulation,
  ServiceCategory,
  useUpdateOrganizationMutation,
  type EditOrganizationRequest,
  type OrgDto,
  type SourcingEventDto,
  type SourcingEventPatchDto,
} from "../../../../../autogen/bff-api";
import { useLoggedInWithOrgContextState } from "../../../../../common/auth/useLoggedInWithOrgContextState";
import {
  coordinatorRegistry,
  useApiErrorEditorConfig,
  useCreateEditor,
  useEditor,
} from "../../../../../common/editing/save-coordinator";
import { RadioOptionGroup } from "../../../../../common/input/RadioOptionGroup";
import { useProcurementTerms } from "../../../../../common/terminology/useProcurementTerms";
import { useAppDispatch } from "../../../../../common/redux/hooks";
import { updateSelectedOrg } from "../../../../../common/redux/reducers/authStateReducer";
import { useRunsPublicProcurements } from "../../../../../common/organization/useRunsPublicProcurements";
import { urls } from "../../../../../urls";
import { saveWithVersionConflictRetry } from "../saveWithVersionConflictRetry";
import {
  foaPartBanner,
  formatThresholdNok,
  impliesSimplifiedFlow,
  isAnnouncedPartI,
  partRunsInSimplifiedFlow,
  regulationSupportsPublishing,
  simplifiedFlowThresholdNok,
} from "../utils/foaPartDisplay";
import { REGULATION_OPTIONS } from "../../../../../common/procurement/publicProcurementOptions";
import { FieldSaveError } from "./FieldSaveError";
import { ProcedureChangedNotice } from "./ProcedureChangedNotice";
import { ProcedureUnavailableNotice } from "./ProcedureUnavailableNotice";
import { ReclassificationNotice } from "./ReclassificationNotice";
import { RegimeMismatchWarning } from "./RegimeMismatchWarning";
import type { ReclassificationTracker } from "./useReclassification";

interface Props {
  eventId: string;
  reclassificationTracker: ReclassificationTracker;
  regimeWarningShownByValueField?: boolean;
  onRegulationChosen?: () => void;
}

/**
 * Everything the classification is and everything that follows from it lives here, so the buyer has
 * one place to look: the regime, the part, whichever input is still missing, and — when the value
 * has drifted away from the regime the procurement holds — what that means and how to act on it.
 */
export const ClassificationPanel = ({
  eventId,
  reclassificationTracker,
  regimeWarningShownByValueField = false,
  onRegulationChosen,
}: Props) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const runsPublicProcurements = useRunsPublicProcurements();
  const terms = useProcurementTerms();
  const { me, selectedOrg } = useLoggedInWithOrgContextState();
  const { state, updateField, fieldStatus } = useEditor<SourcingEventDto, SourcingEventPatchDto>(eventId);
  const [editOrg] = useUpdateOrganizationMutation();
  const companyProfileUrl = urls.app.organizationProfileEdit;

  const publicContext = state.eventContext.type === "Public" ? state.eventContext : null;
  const isClassified = state.eventContext.type !== "Unclassified";
  const isPrivate = state.eventContext.type === "Private";

  const canEditOrg = selectedOrg.canEdit;
  // The firmakort is a preview of what an unclassified draft will inherit, never a stand-in for what
  // a classified one holds: falling back there too would show a procurement a regulation it does not
  // have, and hide that it still needs one.
  const regulation = isClassified ? publicContext?.regulation ?? null : selectedOrg.defaultRegulation ?? null;
  const buyerLegalType = isClassified
    ? publicContext?.buyerLegalType ?? null
    : selectedOrg.buyerLegalType ?? null;
  const foaPart = publicContext?.foaPart ?? null;
  const estimatedValueNok =
    state.eventContext.estimatedValue?.value != null ? Number(state.eventContext.estimatedValue.value) : null;
  const banner = foaPartBanner({
    regulation,
    foaPart,
    estimatedValueNok,
    contractNature: state.eventContext.contractNature,
    buyerLegalType,
    specialServicesAboveEea: state.specialServicesAboveEea ?? false,
    noticeGoesToTed: state.contractNoticeRoute === NoticeRoute.Eu,
    serviceCategory: publicContext?.serviceCategory,
    announcedOnDoffin: publicContext?.announcedOnDoffin,
  });
  const isSavingRegulation = ["saving", "pending"].includes(fieldStatus("publicContext.regulation"));
  const isSavingServiceCategory = ["saving", "pending"].includes(fieldStatus("publicContext.serviceCategory"));
  const serviceCategory = publicContext?.serviceCategory ?? null;
  const suggestedServiceCategory = state.suggestedServiceCategory ?? null;
  const offersServiceCategory =
    publicContext != null &&
    (regulation === Regulation.Foa || regulation === Regulation.Forsyningsforskriften) &&
    state.eventContext.contractNature === ContractNature.Services;
  const underUtilities = regulation === Regulation.Forsyningsforskriften;

  const { reclassification, dismiss: dismissReclassification, expect: expectReclassification } =
    reclassificationTracker;


  // The organisation has never answered the question, so the first procurement to ask it also
  // records the answer. Later changes are this procurement's alone — a buyer with a utility
  // activity switches per contract, not per organisation.
  const adoptsAsOrgDefault = selectedOrg.defaultRegulation == null && canEditOrg;
  // An unclassified draft can only write the organisation's default, so offering "Endre" there would
  // quietly move every future procurement. Overriding one procurement waits until it is classified.
  const canChangeRegulation = isClassified || adoptsAsOrgDefault;

  const editorErrorConfig = useApiErrorEditorConfig();
  useCreateEditor<OrgDto, EditOrganizationRequest>(selectedOrg.id, {
    initialState: selectedOrg,
    save: async (fields) => {
      await coordinatorRegistry.flush(eventId);
      const updatedOrg = await saveWithVersionConflictRetry({
        fields,
        save: (retryFields) =>
          editOrg({ orgId: selectedOrg.id, editOrganizationRequest: retryFields }).unwrap(),
        reloadLatest: () => coordinatorRegistry.flush(eventId),
      });
      dispatch(updateSelectedOrg(updatedOrg));
      return updatedOrg;
    },
    ...editorErrorConfig,
  });
  const { updateField: updateOrgField, fieldStatus: orgFieldStatus } = useEditor<OrgDto, EditOrganizationRequest>(
    selectedOrg.id,
  );

  /**
   * A classified procurement holds its own regulation, so the choice always lands there — writing
   * only the organisation default would leave the buyer looking at the value they just replaced.
   * An organisation that has never answered also gets the answer recorded as its default, and an
   * unclassified draft has nowhere else to put it, so there the default is the only write.
   */
  const chooseRegulation = (value: Regulation) => {
    onRegulationChosen?.();
    if (!isClassified) {
      updateOrgField({ defaultRegulation: { value } });
      return;
    }
    expectReclassification("regulation", value === regulation ? null : foaPart);
    updateField({ publicContext: { regulation: value } });
    if (adoptsAsOrgDefault) updateOrgField({ defaultRegulation: { value } });
  };

  const chooseServiceCategory = (value: ServiceCategory) => {
    expectReclassification("serviceCategory", foaPart);
    updateField({ publicContext: { serviceCategory: value } });
  };

  const chooseBuyerLegalType = (value: BuyerLegalType) => {
    updateOrgField({ buyerLegalType: { value } });
  };

  const isSavingUtilityActivity = ["saving", "pending"].includes(fieldStatus("publicContext.utilityActivity"));

  const regimeLabel = isPrivate
    ? terms.simplifiedRegime
    : isClassified
      ? t("Public procurement")
      : t("Not classified yet");
  const partLabel = isPrivate
    ? null
    : foaPart === FoaPart.PartIi && offersServiceCategory && serviceCategory === ServiceCategory.SpecialServices
      ? t("Part II, special services")
      : foaPart
        ? t(foaPart)
        : isClassified
          ? t("Not classified yet")
          : null;

  const band = isPrivate ? null : state.classificationBand;
  const bandText =
    band?.fromNok != null && band.belowNok != null
      ? t("Thresholds: from {{from}} NOK, below {{below}} NOK", {
          from: formatThresholdNok(band.fromNok),
          below: formatThresholdNok(band.belowNok),
        })
      : band?.fromNok != null
        ? t("Threshold: from {{from}} NOK", { from: formatThresholdNok(band.fromNok) })
        : band?.belowNok != null
          ? t("Threshold: below {{below}} NOK", { below: formatThresholdNok(band.belowNok) })
          : null;

  return (
    <Box mt="6" borderWidth="1px" borderRadius="md" p="4">
      <Box aria-live="polite" aria-atomic="true">
      <Flex align="baseline" gap="2" wrap="wrap">
        <Text fontSize="sm" color="smMuted">
          {t("Classification")}
        </Text>
        <Text fontSize="md" fontWeight="semibold">
          {regimeLabel}
        </Text>
        {partLabel && (
          <>
            <Text fontSize="md" color="smMuted">
              ·
            </Text>
            <Text fontSize="md" fontWeight="semibold">
              {partLabel}
            </Text>
          </>
        )}
      </Flex>
      {!isPrivate && (
        <Text fontSize="xs" color="smMuted" mt="1">
          {banner
            ? t(banner.messageKey, banner.values ?? {})
            : t("Set the estimated value and the contract type to classify the procurement")}
        </Text>
      )}
      {bandText && (
        <Text fontSize="xs" color="smMuted" mt="1">
          {bandText}
        </Text>
      )}
      {!isPrivate && regulation === Regulation.Foa && foaPart === FoaPart.PartIi && (
        <Text fontSize="xs" color="smMuted" mt="1">
          {state.specialServicesAboveEea
            ? t("You set a reasonable tender deadline yourself, and choose whether to use the ESPD. The standstill period is at least 10 days (FOA § 10-2 (2)).")
            : t("You set a reasonable tender deadline and standstill period yourself, and choose whether to use the ESPD.")}
        </Text>
      )}
      </Box>

      {reclassification && reclassification.cause !== "value" && (
        <ReclassificationNotice reclassification={reclassification} onDismiss={dismissReclassification} />
      )}

      <ProcedureChangedNotice
        procedureType={publicContext?.procedureType}
        foaPart={foaPart}
        namesNoProcedure={isAnnouncedPartI(publicContext)}
      />
      <ProcedureUnavailableNotice
        eventId={eventId}
        procedureType={publicContext?.procedureType}
        regulation={regulation}
        foaPart={foaPart}
        contractNature={state.eventContext.contractNature}
        serviceCategory={serviceCategory}
        announcedOnDoffin={publicContext?.announcedOnDoffin}
      />

      {!regimeWarningShownByValueField && <RegimeMismatchWarning eventId={eventId} mt="3" />}

      {!isPrivate && (
        <>
          {canChangeRegulation ? (
            <Accordion allowToggle defaultIndex={regulation == null ? 0 : undefined} mt="3">
              <AccordionItem border="none">
                <AccordionButton px="0" py="2">
                  <Flex flex="1" align="baseline" gap="2" wrap="wrap" textAlign="left">
                    <Text fontSize="xs" color="smMuted">
                      {t("Governing regulation")}
                    </Text>
                    <Text fontSize="xs" fontWeight="medium">
                      {regulation ? t(regulation) : t("Not set")}
                    </Text>
                    {isSavingRegulation && <Spinner size="xs" />}
                  </Flex>
                  <Text fontSize="xs" color="smPrimary" mr="1">
                    {t("Change regulation")}
                  </Text>
                  <AccordionIcon />
                </AccordionButton>
                <AccordionPanel px="0" pb="3">
                  <RadioOptionGroup
                    options={REGULATION_OPTIONS}
                    value={regulation ?? ""}
                    onChange={(value) => chooseRegulation(value as Regulation)}
                  />
                  <Text fontSize="xs" color="smMuted" mt="2">
                    {t(
                      "Which regulation applies follows from what the procurement is for, not from the kind of body you are — the same organisation can have procurements under both",
                    )}
                  </Text>
                  {(adoptsAsOrgDefault || !isClassified) && (
                    <Text fontSize="xs" color="smMuted" mt="2">
                      {t("Your first choice is saved as the default for the whole organisation")}
                    </Text>
                  )}
                  {!regulationSupportsPublishing(regulation) && (
                    <Alert status="info" mt="3" borderRadius="md" fontSize="sm">
                      <AlertIcon />
                      {t("You can register the procurement under this regulation, but announcing it is not supported yet")}
                    </Alert>
                  )}
                </AccordionPanel>
              </AccordionItem>
            </Accordion>
          ) : (
            <Flex align="baseline" gap="2" wrap="wrap" mt="3" py="2">
              <Text fontSize="xs" color="smMuted">
                {t("Governing regulation")}
              </Text>
              <Text fontSize="xs" fontWeight="medium">
                {regulation ? t(regulation) : t("Not set")}
              </Text>
              <Text fontSize="xs" color="smMuted">
                {t("Can be changed per procurement once the procurement is classified")}
              </Text>
            </Flex>
          )}

          {offersServiceCategory && (
            <Box pt="3" borderTopWidth="1px" mt="3">
              <Flex align="center" gap="2" mb="1">
                <Text fontSize="xs" color="smMuted">
                  {t("What kind of service is the contract?")}
                </Text>
                {isSavingServiceCategory && <Spinner size="xs" />}
              </Flex>
              <RadioGroup
                value={serviceCategory ?? ""}
                onChange={(value) => chooseServiceCategory(value as ServiceCategory)}
              >
                <Stack spacing="1">
                  {SERVICE_CATEGORIES.map((category) => (
                    <Radio key={category} value={category} size="sm" colorScheme="teal">
                      <Text fontSize="xs">{serviceCategoryLabel(t, category, underUtilities)}</Text>
                    </Radio>
                  ))}
                </Stack>
              </RadioGroup>
              <FieldSaveError eventId={eventId} path="publicContext.serviceCategory" />
              {serviceCategory == null && suggestedServiceCategory != null && (
                <Flex mt="2" gap="2" align="center" wrap="wrap">
                  <Text fontSize="xs" color="smMuted">
                    {t("The main CPV code suggests: {{category}}", {
                      category: serviceCategoryLabel(t, suggestedServiceCategory, underUtilities),
                    })}
                  </Text>
                  <Button size="xs" variant="link" onClick={() => chooseServiceCategory(suggestedServiceCategory)}>
                    {t("Use the suggestion")}
                  </Button>
                </Flex>
              )}
              <Text fontSize="xs" color="smMuted" mt="1">
                {underUtilities
                  ? t(
                      "Special services are listed by CPV code in Annex 3 of the Utilities Regulation, and health and social services in Annex 4. Both are Part I below NOK 11.6 million and Part III from it.",
                    )
                  : t(
                      "Special services are listed by CPV code in FOA Annex 2, and health, social and education services in FOA Annex 3.",
                    )}
              </Text>
            </Box>
          )}

          {isClassified && regulation === Regulation.Forsyningsforskriften && (
            <Box pt="3" borderTopWidth="1px" mt="3">
              <Flex align="baseline" gap="2" wrap="wrap">
                <Text fontSize="xs" color="smMuted">
                  {t("Utility activity")}
                </Text>
                <Text fontSize="xs" fontWeight="medium">
                  {publicContext?.utilityActivity ? t(publicContext.utilityActivity) : t("Not set")}
                </Text>
                {isSavingUtilityActivity && <Spinner size="xs" />}
              </Flex>
              <Box mt="2">
                <Select
                  size="sm"
                  maxW="sm"
                  value={publicContext?.utilityActivity ?? ""}
                  placeholder={t("Select the utility activity the procurement serves") ?? ""}
                  onChange={(e) =>
                    e.target.value &&
                    updateField({ publicContext: { utilityActivity: e.target.value as UtilityActivity } })
                  }
                >
                  {Object.values(UtilityActivity).map((activity) => (
                    <option key={activity} value={activity}>
                      {t(activity)}
                    </option>
                  ))}
                </Select>
                <Text fontSize="xs" color="smMuted" mt="1">
                  {t("Announced on the notice as the activity of the contracting entity")}
                </Text>
              </Box>
            </Box>
          )}

          <Box pt="3" borderTopWidth="1px">
            <Flex align="baseline" gap="2" wrap="wrap">
              <Text fontSize="xs" color="smMuted">
                {t("Buyer legal type")}
              </Text>
              <Text fontSize="xs" fontWeight="medium">
                {buyerLegalType ? t(buyerLegalType) : t("Not set")}
              </Text>
              {["saving", "pending"].includes(orgFieldStatus("buyerLegalType")) && <Spinner size="xs" />}
              {buyerLegalType && me.isSystemAdmin && (
                <Link as={RouterLink} to={companyProfileUrl} fontSize="xs" color="smPrimary">
                  {t("Edit in the company profile")}
                </Link>
              )}
            </Flex>
            {!buyerLegalType && me.isSystemAdmin && (
              <Box mt="2">
                <Select
                  size="sm"
                  maxW="sm"
                  value=""
                  aria-label={t("Buyer legal type") ?? undefined}
                  placeholder={t("Select the type of body you are") ?? ""}
                  onChange={(e) => e.target.value && chooseBuyerLegalType(e.target.value as BuyerLegalType)}
                >
                  {Object.values(BuyerLegalType).map((type) => (
                    <option key={type} value={type}>
                      {t(type)}
                    </option>
                  ))}
                </Select>
                <Text fontSize="xs" color="smMuted" mt="1">
                  {t("Applies to the whole organisation — it is part of the company profile")}
                </Text>
              </Box>
            )}
            {!buyerLegalType && !me.isSystemAdmin && (
              <Text fontSize="xs" color="smMuted" mt="1">
                {t("The buyer legal type is managed by SourceMagnet — contact us to have it set")}
              </Text>
            )}
          </Box>
        </>
      )}
    </Box>
  );
};
